import Select from "react-select";
import { useState } from "react";
import { PhotoIcon } from "@heroicons/react/24/solid";
import { ErrorAlert, SuccessAlert } from "../../components/alerts";
import {
  createAppBanner,
  getSingleUploadPresignedS3Url,
  uploadToS3PresignedUrl,
} from "../../api/new-api";
import { useNavigate } from "react-router";
import { PELinearProgressWithLabel } from "../../components/linear-progress";
import { PhonePreview } from "../../components/PhonePreview";
import { userState } from "../../state/UserState";
import DateComponent from "../../components/DateComponents";
import { daySelectOptions } from "../../constant/attractor-select";
import { useColors } from "../../provider/colors-provider";
import UploadImageContainer from "../../components/UploadImageContainer";
import CustomInput from "../../components/CustomInput";
import moment from "moment";

// Simple CSS for the loader animation
const loaderStyles = `
  .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 20px auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .success-checkmark {
    display: inline-block;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: #28a745;
    position: relative;
    margin: 20px auto;
  }

  .success-checkmark::before {
    content: "";
    position: absolute;
    top: 15px;
    left: 20px;
    width: 10px;
    height: 20px;
    border: solid white;
    border-width: 0 4px 4px 0;
    transform: rotate(45deg);
  }

  .thumbnail-image {
    max-width: 300px;
    width: 100%;
    height: auto;
    margin: 20px 0;
    border-radius: 8px;
  }
`;

const MobileBannerCreator = () => {
  const { primary } = useColors();
  const navigate = useNavigate();

  // Form states
  const [startDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const [endDateManuallyChanged, setEndDateManuallyChanged] =
    useState<boolean>(false);
  const [selected, setSelected] = useState<any>([]);
  const [bannerType, setBannerType] = useState<
    "Fullscreen" | "WebsiteLink" | null
  >(null);
  const [data, setData] = useState<any>({ displayLinkUrl: "" });
  const [bannerFile, setBannerFile] = useState<null | File>(null);
  const [fullscreenBlobUrl, setFullscreenBlobUrl] = useState<null | string>(
    null
  );
  const [thumbnailFile, setThumbnailFile] = useState<null | File>(null);
  const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState<null | string>(null);
  const [error, setError] = useState<any>("");
  const [thumbnailError, setThumbnailError] = useState<any>("");
  const [fullscreenError, setFullscreenError] = useState<any>("");
  const [success, setSuccess] = useState<any>("");
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [showEveryday, setShowEveryday] = useState<any>(false);
  const [isUrlValid, setIsUrlValid] = useState(true);

  // New state to manage screen
  const [screenState, setScreenState] = useState<
    "form" | "loading" | "success" | "error"
  >("form");

  const handleFileChange = (event: {
    target: { files: any[]; name: string };
  }) => {
    const file = event.target.files[0];
    if (file) {
      setFullscreenError("");
      setThumbnailError("");
      setError("");

      const blobUrl = URL.createObjectURL(file);
      const img = new Image();
      img.src = blobUrl;
      img.onload = () => {
        if (event.target.name === "thumbnailBlobUrl") {
          if (img.width / img.height !== 6 / 1) {
            setThumbnailError("Thumbnail image ratio should be 6:1");
            return;
          }
          setThumbnailFile(file);
          setThumbnailBlobUrl(blobUrl);
          return;
        }
        if (event.target.name === "bannerFile") {
          const aspectRatio = img.width / img.height;
          const mobileAspectRatio = 9 / 16;
          if (Math.abs(aspectRatio - mobileAspectRatio) < 1) {
            setBannerFile(file);
            setFullscreenBlobUrl(blobUrl);
          } else {
            setFullscreenError(
              "Image aspect ratio does not fit a mobile screen size."
            );
            return;
          }
        }
      };
    }
  };

  const validate = () => {
    if (!thumbnailFile) {
      setError("Please upload thumbnail image");
      return true;
    }
    if (!bannerType) {
      setError("Please select banner type.");
      return true;
    }
    if (bannerType === "Fullscreen" && !bannerFile) {
      setError("Please upload fullscreen image");
      return true;
    }
    if (bannerType === "WebsiteLink" && (!data.displayLinkUrl || !isUrlValid)) {
      setError("Please enter a valid URL");
      return true;
    }
    if (!startDate) {
      setError("Please enter start date");
      return true;
    }
    if (!endDate) {
      setError("Please enter end date");
      return true;
    }
    if (selected.length === 0) {
      setError("Please select show on days");
      return true;
    }
    setError("");
    return false;
  };

  const handleSubmit = async () => {
    const hasErrors = validate();
    if (hasErrors) {
      return;
    }

    try {
      setScreenState("loading");
      setPercentCompleted(2);

      const presignedUrlResponseThumbnail = await getSingleUploadPresignedS3Url(
        {
          imageFileName: thumbnailFile!.name,
        }
      );
      const uploadUrlThumbnail =
        presignedUrlResponseThumbnail.data.imageUploadUrl;
      const mediaS3KeyThumbnail = presignedUrlResponseThumbnail.data.imageKey;

      await uploadToS3PresignedUrl({
        uploadUrl: uploadUrlThumbnail,
        data: thumbnailFile,
        progressCallback: (percentCompleted) => {
          console.log(`Thumbnail upload progress: ${percentCompleted}%`);
          setPercentCompleted(percentCompleted / 2);
        },
      });

      let mediaS3KeyBanner = null;
      if (bannerType === "Fullscreen") {
        const presignedUrlResponseBanner = await getSingleUploadPresignedS3Url({
          imageFileName: bannerFile!.name,
        });
        const uploadUrlBanner = presignedUrlResponseBanner.data.imageUploadUrl;
        mediaS3KeyBanner = presignedUrlResponseBanner.data.imageKey;

        await uploadToS3PresignedUrl({
          uploadUrl: uploadUrlBanner,
          data: bannerFile,
          progressCallback: (percentCompleted) => {
            console.log(`Fullscreen upload progress: ${percentCompleted}%`);
            setPercentCompleted(50 + percentCompleted / 2);
          },
        });
      }

      await createAppBanner({
        displayLinkUrl: bannerType === "WebsiteLink" ? data.displayLinkUrl : "",
        mediaThumbS3Key: mediaS3KeyThumbnail,
        mediaFullS3Key: mediaS3KeyBanner,
        startDateEpoch: new Date(startDate).getTime(),
        endDateEpoch: new Date(endDate).getTime(),
        dayVisibility: selected.map((val: any) => val.value).join(","),
        bannerType: bannerType ?? "",
      });

      setPercentCompleted(100);
      setScreenState("success");
      setTimeout(() => {
        navigate("/mobile-banners");
      }, 500); // 0.5-second delay before navigation
    } catch (error) {
      console.error("An error occurred:", error);
      setScreenState("error");
      setError("An error occurred. Please try again or cancel.");
    }
  };

  // Render the form screen
  const renderForm = () => (
    <div className="w-full">
      <div className="my-4 block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
        <div className="dropzone flex-cen-stack">
          <div className="flex flex-row gap-2 justify-center">
            <div className="w-full flex justify-center">
              <PhonePreview
                screenType="MY_HOME"
                userState={userState}
                myHomeAppBanner={{ fullscreenS3Key: thumbnailBlobUrl } as any}
              />
            </div>
            <div className="w-full">
              <UploadImageContainer
                id="thumbnailBlobUrl"
                label="Upload Thumbnail Image"
                blobUrl={thumbnailBlobUrl}
                errorMessage={thumbnailError}
                handleFileChange={handleFileChange}
                description="Image aspect ratio should be 6:1. Example: 600x100"
              />

              <div className="my-4">
                <p className="text-sm font-bold my-2">BANNER TYPE:</p>
                <Select
                  options={[
                    { value: "Fullscreen", label: "Fullscreen Image" },
                    { value: "WebsiteLink", label: "Link to Website" },
                  ]}
                  value={
                    bannerType
                      ? {
                          value: bannerType,
                          label:
                            bannerType === "Fullscreen"
                              ? "Fullscreen Image"
                              : "Link to Website",
                        }
                      : null
                  }
                  onChange={(option) =>
                    setBannerType(
                      option?.value as "Fullscreen" | "WebsiteLink" | null
                    )
                  }
                  placeholder="Select banner type"
                />
              </div>

              {bannerType === "Fullscreen" && (
                <UploadImageContainer
                  id="bannerFile"
                  label="Upload Fullscreen Image"
                  blobUrl={fullscreenBlobUrl}
                  errorMessage={fullscreenError}
                  handleFileChange={handleFileChange}
                  description="Image aspect ratio should be close to 9:16. Example: 1080x1920"
                />
              )}
              {bannerType === "WebsiteLink" && (
                <div className="my-4 flex flex-col">
                  <p className="text-sm font-bold my-2">LINK TO WEBSITE</p>
                  <CustomInput
                    value={data.displayLinkUrl}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const urlPattern = /^https?:\/\/.+?\..+?$/;
                      const isValid =
                        newValue === "" || urlPattern.test(newValue);
                      setIsUrlValid(isValid);
                      setData((prevData: any) => ({
                        ...prevData,
                        displayLinkUrl: newValue,
                      }));
                    }}
                    type="text"
                    placeholder="e.g., https://example.com"
                  />
                  {!isUrlValid && (
                    <p className="text-sm text-red-500 mt-1">
                      URL must start with http:// or https:// and include a
                      domain (e.g., example.com)
                    </p>
                  )}
                </div>
              )}

              <div className="my-2">
                <p className="text-sm font-bold my-2">START DATE:</p>
                <DateComponent
                  value={startDate}
                  setter={(date: any) => {
                    // Check if start date is after end date
                    if (endDate && date > endDate) {
                      alert(
                        "Start date should be before or equal to end date. Setting to 1 day before end date."
                      );
                      // Set start date to 1 day before end date
                      const adjustedStartDate = moment(endDate)
                        .subtract(1, "day")
                        .toDate();
                      setStartDate(adjustedStartDate);
                      return;
                    }
                    setStartDate(date);
                    // Only auto-set end date if it hasn't been manually changed
                    if (!endDateManuallyChanged) {
                      const minimumEndDate = moment(date)
                        .add(14, "days")
                        .toDate();
                      setEndDate(minimumEndDate);
                    }
                  }}
                />
              </div>
              <div className="my-2">
                <p className="text-sm font-bold my-2">END DATE:</p>
                <DateComponent
                  value={endDate}
                  setter={(date: any) => {
                    if (date <= startDate) {
                      alert("End date should be greater than start date");
                      const minimumEndDate = moment(startDate)
                        .add(14, "days")
                        .toDate();
                      setEndDate(minimumEndDate);
                      return;
                    }
                    setEndDate(date);
                    setEndDateManuallyChanged(true);
                    setError("");
                  }}
                />
              </div>
              <div className="my-2 flex gap-4 items-center justify-between">
                <p className="text-sm font-bold">SHOWING DAYS:</p>
                <span className="flex items-center gap-2">
                  <p className="text-sm font-bold">SHOW EVERYDAY:</p>
                  <input
                    checked={showEveryday}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected(daySelectOptions);
                        setShowEveryday(true);
                      } else {
                        setSelected([]);
                        setShowEveryday(false);
                      }
                    }}
                    id="checked-checkbox"
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </span>
              </div>
              <div className="my-2">
                <Select
                  isDisabled={showEveryday}
                  isMulti
                  value={selected}
                  options={daySelectOptions}
                  onChange={(newValue) => setSelected(newValue)}
                />
              </div>
              <div className="my-2 flex flex-row gap-6 justify-between">
                <button
                  onClick={handleSubmit}
                  type="button"
                  className="hover:opacity-80 w-full rounded-md bg-primary-900 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                  style={{ backgroundColor: primary }}
                >
                  {"Create Mobile Banner"}
                </button>
                <button
                  onClick={() => navigate("/mobile-banners")}
                  type="button"
                  className="w-full rounded-md bg-gray-400 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-300"
                >
                  {"Cancel"}
                </button>
              </div>
              {error && <ErrorAlert message={error} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render the loading screen with thumbnailBlobUrl as an image
  const renderLoading = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-6">
      <div className="loader" />
      <p className="text-lg font-semibold mb-4">Creating App Banner</p>
      {thumbnailBlobUrl && (
        <img
          src={thumbnailBlobUrl}
          alt="Thumbnail Preview"
          className="thumbnail-image"
        />
      )}
      <PELinearProgressWithLabel value={percentCompleted} />
    </div>
  );

  // Render the success screen
  const renderSuccess = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-6">
      <div className="success-checkmark" />
      <p className="text-lg font-semibold text-green-600">
        Successfully created app banner
      </p>
      {thumbnailBlobUrl && (
        <img
          src={thumbnailBlobUrl}
          alt="Thumbnail Preview"
          className="thumbnail-image"
        />
      )}
    </div>
  );

  // Render the error screen
  const renderError = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-6">
      <p className="text-lg font-semibold text-red-600 mb-4">{error}</p>
      {thumbnailBlobUrl && (
        <img
          src={thumbnailBlobUrl}
          alt="Thumbnail Preview"
          className="thumbnail-image"
        />
      )}
      <div className="mt-10 flex flex-row gap-6 justify-center">
        <button
          onClick={() => {
            setError(""); // Clear error
            handleSubmit(); // Retry the save operation
          }}
          type="button"
          className="hover:opacity-80 w-32 rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
        >
          Retry
        </button>
        <button
          onClick={() => navigate("/mobile-banners")}
          type="button"
          className="w-32 rounded-md bg-gray-400 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <>
      <style>{loaderStyles}</style>
      {screenState === "form" && renderForm()}
      {screenState === "loading" && renderLoading()}
      {screenState === "success" && renderSuccess()}
      {screenState === "error" && renderError()}
    </>
  );
};

export default MobileBannerCreator;
